import { ServiceRequestStatusCode, AcceptedOfferSummary } from "../../models/models";
import { ServiceCompletionDetails, ProgressUpdate, UpdateType } from "../../models/executionModels";
import { ServiceReview } from "../../models/reviewModels";
import { OfferStatusCode } from "../../../serviceOffers/models/models";

// ===================================================================
//  Mapping: MilestoneType → backend ProgressUpdateType for LOG-4
// ===================================================================
const milestoneToUpdateType: Partial<Record<MilestoneType, UpdateType>> = {
  offerAccepted: UpdateType.OfferAccepted,
  serviceConfirmed: UpdateType.ProviderConfirmed,
  workStarted: UpdateType.WorkStarted,
  workCompleted: UpdateType.WorkCompleted,
  ownerConfirmed: UpdateType.OwnerConfirmed,
  reviewLeft: UpdateType.ReviewReceived,
};

// ===================================================================
//  Types
// ===================================================================

export type MilestoneType =
  | "published"
  | "offerAccepted"
  | "serviceConfirmed"
  | "workStarted"
  | "workCompleted"
  | "ownerConfirmed"
  | "reviewLeft";

export type MilestoneStatus = "completed" | "current" | "pending";

export interface LogbookMilestoneEntry {
  id: string;
  kind: "milestone";
  type: MilestoneType;
  ordinal: number;
  timestamp?: string;
  status: MilestoneStatus;
  data: Record<string, any>;
}

export interface LogbookProgressNote {
  id: string;
  kind: "progressNote";
  timestamp: string;
  authorName: string;
  message: string;
}

export type LogbookEntry = LogbookMilestoneEntry | LogbookProgressNote;

// ===================================================================
//  Color map per milestone type
// ===================================================================

export const milestoneColorMap: Record<MilestoneType, string> = {
  published: "#3B82F6",
  offerAccepted: "#F59E0B",
  serviceConfirmed: "#10B981",
  workStarted: "#0EA5E9",
  workCompleted: "#059669",
  ownerConfirmed: "#10B981",
  reviewLeft: "#FFB400",
};

// ===================================================================
//  Status code → current milestone mapping
// ===================================================================

function getCurrentMilestoneType(
  statusCode: ServiceRequestStatusCode,
  acceptedOffer?: AcceptedOfferSummary | null,
  hasReviewed?: boolean
): MilestoneType {
  switch (statusCode) {
    case ServiceRequestStatusCode.openForOffers:
    case ServiceRequestStatusCode.negotiating:
      return "published";
    case ServiceRequestStatusCode.awaitingProviderConfirmation: {
      // Check if provider has confirmed (offer status >= Confirmed)
      const offerStatus = acceptedOffer?.statusCode;
      if (offerStatus && offerStatus >= OfferStatusCode.confirmed) {
        return "serviceConfirmed";
      }
      return "offerAccepted";
    }
    case ServiceRequestStatusCode.inProgress: {
      const os = acceptedOffer?.statusCode;
      if (os === OfferStatusCode.workInProgress) return "workStarted";
      if (os === OfferStatusCode.confirmed) return "serviceConfirmed";
      return "workStarted";
    }
    case ServiceRequestStatusCode.awaitingCompletionConfirmation:
      return "workCompleted";
    case ServiceRequestStatusCode.completed:
      // Always show reviewLeft as current — if reviewed it shows the review,
      // if not it shows the "Leave Review" prompt. ownerConfirmed shows as completed.
      return "reviewLeft";
    case ServiceRequestStatusCode.archived:
      return "reviewLeft";
    default:
      return "published";
  }
}

// ===================================================================
//  Milestone ordinal lookup
// ===================================================================

const MILESTONE_ORDINALS: Record<MilestoneType, number> = {
  published: 0,
  offerAccepted: 1,
  serviceConfirmed: 2,
  workStarted: 3,
  workCompleted: 4,
  ownerConfirmed: 5,
  reviewLeft: 6,
};

// ===================================================================
//  Builder function
// ===================================================================

export interface BuildLogbookParams {
  statusCode: ServiceRequestStatusCode;
  createdDate?: string;
  offersCount: number;
  newOffersCount: number;
  acceptedOffer?: AcceptedOfferSummary | null;
  completion?: ServiceCompletionDetails | null;
  progressUpdates: ProgressUpdate[];
  reviews: ServiceReview[];
  hasReviewed: boolean;
}

export function buildLogbookEntries(params: BuildLogbookParams): LogbookEntry[] {
  const {
    statusCode,
    createdDate,
    offersCount,
    newOffersCount,
    acceptedOffer,
    completion,
    progressUpdates,
    reviews,
    hasReviewed,
  } = params;

  const currentType = getCurrentMilestoneType(statusCode, acceptedOffer, hasReviewed);
  const currentOrdinal = MILESTONE_ORDINALS[currentType];

  function milestoneStatus(type: MilestoneType): MilestoneStatus {
    const ord = MILESTONE_ORDINALS[type];
    if (ord < currentOrdinal) return "completed";
    if (ord === currentOrdinal) return "current";
    return "pending";
  }

  // LOG-4: Build a map of backend milestone timestamps (prefer over synthesized)
  const backendMilestoneTimestamps: Partial<Record<MilestoneType, string>> = {};
  for (const [mType, uType] of Object.entries(milestoneToUpdateType) as [MilestoneType, UpdateType][]) {
    const backendRow = progressUpdates.find((u) => u.updateType === uType);
    if (backendRow) {
      backendMilestoneTimestamps[mType] = backendRow.createdDate;
    }
  }

  // Helper: pick backend milestone timestamp if available, else fallback
  function pickTimestamp(type: MilestoneType, fallback?: string): string | undefined {
    return backendMilestoneTimestamps[type] || fallback;
  }

  const entries: LogbookEntry[] = [];

  // 1. Published: Collecting Offers — always shown (SR is at least published)
  entries.push({
    id: "ms-published",
    kind: "milestone",
    type: "published",
    ordinal: 0,
    timestamp: createdDate,
    status: milestoneStatus("published"),
    data: { offersCount, newOffersCount },
  });

  // 2. Offer Accepted
  const acceptedStatus = milestoneStatus("offerAccepted");
  entries.push({
    id: "ms-offerAccepted",
    kind: "milestone",
    type: "offerAccepted",
    ordinal: 1,
    timestamp: acceptedStatus !== "pending" && acceptedOffer
      ? pickTimestamp("offerAccepted", acceptedOffer.acceptedDate || acceptedOffer.statusChangedDate)
      : undefined,
    status: acceptedStatus,
    data: acceptedStatus !== "pending" && acceptedOffer
      ? {
          offerId: acceptedOffer.offerId,
          providerName: acceptedOffer.providerName,
          providerLogoUrl: acceptedOffer.providerLogoUrl,
          providerId: acceptedOffer.providerId,
          totalEstimatedCost: acceptedOffer.totalEstimatedCost,
          earliestStartDate: acceptedOffer.earliestStartDate,
          estimatedDuration: acceptedOffer.estimatedDuration,
          messageToCustomer: acceptedOffer.messageToCustomer,
          offerFiles: acceptedOffer.files,
        }
      : {},
  });

  // 3. Service Confirmed
  const confirmedStatus = milestoneStatus("serviceConfirmed");
  entries.push({
    id: "ms-serviceConfirmed",
    kind: "milestone",
    type: "serviceConfirmed",
    ordinal: 2,
    timestamp: confirmedStatus !== "pending" && acceptedOffer
      ? pickTimestamp("serviceConfirmed", acceptedOffer.confirmedByProviderDate)
      : undefined,
    status: confirmedStatus,
    data: confirmedStatus !== "pending" && acceptedOffer
      ? {
          providerName: acceptedOffer.providerName,
          providerLogoUrl: acceptedOffer.providerLogoUrl,
          providerId: acceptedOffer.providerId,
          confirmedArrivalDate: acceptedOffer.confirmedArrivalDate,
          confirmedArrivalTimeFrom: acceptedOffer.confirmedArrivalTimeFrom,
          confirmedArrivalTimeTo: acceptedOffer.confirmedArrivalTimeTo,
          confirmationMessage: acceptedOffer.confirmationMessage,
        }
      : {},
  });

  // 4. Work Started
  const workStartedStatus = milestoneStatus("workStarted");
  entries.push({
    id: "ms-workStarted",
    kind: "milestone",
    type: "workStarted",
    ordinal: 3,
    timestamp: workStartedStatus !== "pending"
      ? pickTimestamp("workStarted", acceptedOffer?.workStartedDate)
      : undefined,
    status: workStartedStatus,
    data: workStartedStatus !== "pending" && acceptedOffer
      ? {
          providerName: acceptedOffer.providerName,
          providerId: acceptedOffer.providerId,
          workStartedDate: acceptedOffer.workStartedDate,
        }
      : {},
  });

  // 4.5: Progress notes — interleaved between Work Started (3) and Work Completed (4)
  const progressNotes = progressUpdates.filter(
    (u) => u.updateType === UpdateType.ProgressNote
  );
  for (const note of progressNotes) {
    entries.push({
      id: `pn-${note.id}`,
      kind: "progressNote",
      timestamp: note.createdDate,
      authorName: note.authorName,
      message: note.message,
    });
  }

  // 5. Work Completed
  const workCompletedStatus = milestoneStatus("workCompleted");
  entries.push({
    id: "ms-workCompleted",
    kind: "milestone",
    type: "workCompleted",
    ordinal: 4,
    timestamp: workCompletedStatus !== "pending"
      ? pickTimestamp("workCompleted", completion?.completedByProviderDate || acceptedOffer?.workCompletedDate)
      : undefined,
    status: workCompletedStatus,
    data: workCompletedStatus !== "pending"
      ? { completion, files: acceptedOffer?.workCompletionFiles }
      : {},
  });

  // 6. Owner Confirmed
  const ownerConfirmedStatus = milestoneStatus("ownerConfirmed");
  entries.push({
    id: "ms-ownerConfirmed",
    kind: "milestone",
    type: "ownerConfirmed",
    ordinal: 5,
    timestamp: ownerConfirmedStatus !== "pending" && completion?.confirmedByOwnerDate
      ? pickTimestamp("ownerConfirmed", completion.confirmedByOwnerDate)
      : undefined,
    status: ownerConfirmedStatus,
    data: {},
  });

  // 7. Review Left
  const reviewStatus = milestoneStatus("reviewLeft");
  entries.push({
    id: "ms-reviewLeft",
    kind: "milestone",
    type: "reviewLeft",
    ordinal: 6,
    timestamp: reviewStatus !== "pending"
      ? pickTimestamp("reviewLeft", reviews.length > 0 ? reviews[0].createdDate : undefined)
      : undefined,
    status: reviewStatus,
    data: reviewStatus !== "pending" ? { reviews, hasReviewed } : {},
  });

  return entries;
}
